import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { channels } from '@/mocks/channels';
import { resolveTone, toneStyles } from '@/utils/tone';

const cadenceOptions = ['全部', '每日更新', '工作日更新', '每周更新'];

export default function ChannelExplorer() {
  const [keyword, setKeyword] = useState('');
  const [cadence, setCadence] = useState('全部');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return channels.filter((c) => {
      const matchCadence = cadence === '全部' || c.cadence === cadence;
      if (!matchCadence) return false;
      if (!kw) return true;
      return (
        c.name.toLowerCase().includes(kw) ||
        c.summary.toLowerCase().includes(kw) ||
        c.tags.some((t) => t.toLowerCase().includes(kw))
      );
    });
  }, [keyword, cadence]);

  return (
    <section id="channel-map" className="bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">频道覆盖地图</h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
              通过关键词或更新节奏快速筛选频道。输入频道名、核心话题都能匹配，例如“维权”“测评”“AI”。
            </p>
          </div>
          <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-3">
            <label className="relative flex items-center">
              <i className="ri-search-line absolute left-3 text-foreground-400" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索频道或话题"
                aria-label="搜索频道或话题"
                className="w-full sm:w-64 bg-background-100 border border-background-300/70 rounded-md pl-9 pr-3 py-2.5 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
              />
            </label>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {cadenceOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setCadence(option)}
              aria-pressed={cadence === option}
              className={`px-4 py-2 rounded-full text-sm font-label transition-colors whitespace-nowrap ${
                cadence === option
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 border border-background-300/60 hover:border-primary-300 hover:text-primary-700'
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-lg border border-background-300/60 bg-background-100/60 px-6 py-14 text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-400" aria-hidden="true" />
            <p className="mt-3 text-sm text-foreground-600">没有匹配的频道，换个关键词试试，或查看全部频道。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setCadence('全部');
              }}
              className="mt-4 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label px-4 py-2.5 rounded-md transition-colors"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((channel) => {
              const tone = toneStyles[resolveTone(channel.tone)];
              return (
                <a
                  key={channel.slug}
                  href={`#channel-${channel.slug}`}
                  className={`group flex flex-col gap-3 rounded-lg border border-background-300/60 bg-background-100/50 p-5 transition-colors ${tone.hoverBorder}`}
                >
                  <span className={`w-11 h-11 rounded-lg flex items-center justify-center ${tone.iconBox}`}>
                    <i className={`${channel.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{channel.name}</h3>
                  <p className="text-xs md:text-sm leading-relaxed text-foreground-600 line-clamp-3">{channel.summary}</p>
                  <span className="mt-auto pt-3 text-xs font-label text-primary-600 inline-flex items-center gap-1">
                    查看频道详情
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </span>
                </a>
              );
            })}
          </div>
        )}

        <p className="mt-6 text-xs font-label text-foreground-500">
          当前显示 {filtered.length} / {channels.length} 个频道 · 点击卡片可跳转到下方频道详情
        </p>
      </div>
    </section>
  );
}